<template>
  <div class="authentication-container">
    <div class="authentication-overlay">
      <div
        id="authentication-content"
        class="authentication-content"
      >
        <div style="text-align: center">
          <mastery-icon />
        </div>
        <div class="box">
          <div class="text-lg font-semibold text-primary title">
            {{ signupTitle }}
          </div>
          <div class="text-sm font-regular text-tertiary subtitle">
            {{ signupSubTitle }}
          </div>
          <auth-mastery-input
            :error="errors['firstName']"
            :field="firstName"
            label="First Name"
            placeholder="Enter your name"
            type="text"
            @enter="submit"
            @inputChange="handleInputChange($event, 'firstName')"
          />
          <auth-mastery-input
            :error="errors['email']"
            :field="email"
            label="Email"
            placeholder="Enter your email"
            type="email"
            @enter="submit"
            @inputChange="handleInputChange($event, 'email')"
          />
          <auth-mastery-input
            :error="errors['password']"
            :field="password"
            label="Password"
            placeholder="Create a password"
            type="password"
            @enter="submit"
            @inputChange="handleInputChange($event, 'password')"
          />
          <activation
            v-if="showActivators"
            :activators="activators"
            :mastery="true"
            :show-expander-button="true"
          />
          <button
            class="button-reset button-primary font-semibold margin-bottom-1"
            :class="{ disabled: loading }"
            data-test="sign-up-button"
            @click="submit"
          >
            Continue
          </button>
          <div class="flexbox-row justify-center">
            <span class="text-sm font-semibold text-primary link-underline">
              Have an account?
              <router-link class="no-visited" :to="{ name: 'mastery#signIn' }">
                Log in
              </router-link>
            </span>
          </div>
          <div class="divider">
            <span class="text-sm text-primary font-semibold">or</span>
          </div>
          <span class="social-buttons">
            <google-circle-button
              :create-account="true"
              :mastery="true"
            />
            <facebook-circle-button
              :create-account="true"
              :mastery="true"
            />
            <apple-circle-button
              :create-account="true"
              :mastery="true"
            />
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Activation from '@components/activation/show';
import AppleCircleButton from '@/components/singletons/AppleCircleButton';
import FacebookCircleButton from '@components/singletons/FacebookCircleButton';
import GoogleCircleButton from '@/components/singletons/GoogleCircleButton';
import MasteryIcon from '@components/mastery/MasteryIcon';
import AuthMasteryInput from '@components/ui/AuthMasteryInput';

import ActivationMixin from '@/mixins/activation';
import AuthenticationMixin from '@/mixins/authentication';

const ACTIVATION_EVENT = 'activation-code-change';
import { setupChecklist, ONBOARDING_CHECKLIST_KEY } from '@/utils/checklist';

export default {
  components: { Activation, AppleCircleButton, FacebookCircleButton, MasteryIcon, AuthMasteryInput, GoogleCircleButton },
  mixins: [ActivationMixin, AuthenticationMixin],
  data() {
    return {
      activationCodes: {},
      activationCodesValid: true,
    };
  },
  computed: {
    activators() {
      return this.$store.getters['activation/activators'];
    },
    showActivators() {
      return this.$store.getters['app/showActivatorsOnSignUp'];
    },
    signupTitle() {
      const prodVar = this.$store.getters['productVars/get']['sign_up_title'];
      return prodVar ? prodVar.value : 'Ready to tailor your learning?';
    },
    signupSubTitle() {
      const prodVar = this.$store.getters['productVars/get']['sign_up_screen_subtitle'];
      return prodVar ? prodVar.value : 'Create your free account to get started.';
    },
  },
  created() {
    if (this.authenticated) {
      this.$router.push({name: 'home'});
    } else {
      this.$eventBus.$on(ACTIVATION_EVENT, this.updateActivationCodes);
    }
  },
  beforeDestroy() {
    this.$eventBus.$off(ACTIVATION_EVENT);
  },
  methods: {
    submit() {
      // clear all validations
      this.clearAuthenticationErrors();

      const params = {
        activationCodes: this.activationCodes,
        user: {
          email: this.email,
          firstName: this.firstName,
          password: this.password,
        },
        ...this.collectTrackingParams(),
      };

      this.loading = true;
      this.$store.dispatch('session/signUp', params).then(results => {
        this.$analytics.events.signUp(this.$store, 'Native');
        setupChecklist(this.$store, ONBOARDING_CHECKLIST_KEY);
        this.postSignUp();
      }).catch(e => {
        this.handleErrors(e, 'Sign Up');
        this.loading = false;
      });
    },
  },
};
</script>

<style lang='scss' scoped>
  @import '@/style/authentication-v2.scss';
</style>

<style lang="scss">
#authentication-content {
  label {
    display: block;
    font-size: var(--Fontsizetext-sm);
    font-weight: var(--Fontweightfont-weight-medium);
    margin-bottom: rem-calc(4);
  }
  .activator input {
    padding: 0.75rem;
    background-color: white;
    border-radius: var(--radius-md);
    border: 1px solid var(--border-primary);
    font-size: 1rem;
    color: #333;
  }

  .margin-bottom-1 {
    margin-bottom: rem-calc(16);
  }
}
</style>
